// Reports (PLAN §10.1; task 31): the report query DSL, its validation against the schema registry, a plain-English
// description of a query, the result shape, the flat table used by CSV/XLSX exports, and report templates
// (admin-defined section structures; the seeded one follows the sample synthesis note).
//
// The DSL is JSON. The claims service (`packages/db/src/claims/report-query.ts`) compiles it to parameterized SQL:
// every value in a query travels as a bound parameter, never as SQL text. What a query can express is closed —
// kinds, types, names, review status, sensitivity, tags, field values and relations (with the entity at the other
// end described by a nested set) — so a saved report is data and can't carry code.

import type { ReviewStatus } from './claims';
import { isReviewStatus, isSensitivity } from './claims';
import { RELIABILITY_LABELS, type ReliabilityStatus } from './reliability';
import type { ReportLanguage } from './report-language';
import { groupsForType, typeChain } from './schema/resolve';
import type { EntityKind, FieldDef, FieldType, RelationTypeDef, SchemaSnapshot } from './schema/types';
import { ENTITY_KIND_LABELS, isEntityKind, isKey } from './schema/types';
import { formatFieldValue } from './schema/values';
import type { Sensitivity } from './uploads';

export const REPORT_QUERY_VERSION = 1;

export const REPORT_LIMITS = {
  /** Conditions in one query, nested sets included. */
  conditions: 16,
  /** Nesting of "the entity at the other end" sets. */
  depth: 2,
  /** Entity ids / list values / tags per condition. */
  items: 50,
  text: 200,
  /** Extra field columns. */
  columns: 8,
  /** Rows a report shows. */
  rows: 1000,
  /** Rows one CSV/XLSX export may hold. */
  exportRows: 5000,
  name: 120,
  description: 1000,
  // Report v2 (the report as a document): figures per report, a figure caption, the analyst's bibliography entries
  // (count and characters each) and the free-text brief a report is asked for.
  figures: 40,
  captionChars: 300,
  bibliographyEntries: 100,
  bibliographyChars: 600,
  briefChars: 2000,
} as const;

export const REPORT_MATCHES = ['all', 'any'] as const;
export type ReportMatch = (typeof REPORT_MATCHES)[number];

export const RELATION_DIRECTIONS = ['out', 'in', 'either'] as const;
export type RelationDirection = (typeof RELATION_DIRECTIONS)[number];

export const REPORT_FIELD_OPS = [
  'any_of',
  'none_of',
  'contains',
  'equals',
  'at_least',
  'at_most',
  'between',
  'in_period',
  'is',
  'exists',
  'missing',
] as const;
export type ReportFieldOp = (typeof REPORT_FIELD_OPS)[number];

export const REPORT_FIELD_OP_LABELS: Record<ReportFieldOp, string> = {
  any_of: 'is any of',
  none_of: 'is none of',
  contains: 'contains',
  equals: 'is exactly',
  at_least: 'is at least',
  at_most: 'is at most',
  between: 'is between',
  in_period: 'falls in',
  is: 'links to',
  exists: 'has a value',
  missing: 'has no value',
};

const TEXTUAL: ReportFieldOp[] = ['contains', 'equals', 'exists', 'missing'];
/** Which operators each field type supports (rating compares the 1–5 value; money its bounds). */
export const REPORT_FIELD_OPS_BY_TYPE: Record<FieldType, readonly ReportFieldOp[]> = {
  text: TEXTUAL,
  longtext: ['contains', 'exists', 'missing'],
  url: TEXTUAL,
  email: TEXTUAL,
  phone: TEXTUAL,
  number: ['at_least', 'at_most', 'between', 'exists', 'missing'],
  rating: ['at_least', 'at_most', 'between', 'exists', 'missing'],
  money_range: ['at_least', 'at_most', 'exists', 'missing'],
  fuzzy_date: ['in_period', 'exists', 'missing'],
  enum: ['any_of', 'none_of', 'exists', 'missing'],
  multi_enum: ['any_of', 'none_of', 'exists', 'missing'],
  entity_ref: ['is', 'contains', 'exists', 'missing'],
  place_ref: ['is', 'contains', 'exists', 'missing'],
  social_handles: ['contains', 'exists', 'missing'],
  table: ['exists', 'missing'],
};

export interface NameCondition {
  type: 'name';
  /** Any name, alias or transliteration, or part of the ID code. */
  text: string;
}
export interface StatusCondition {
  type: 'status';
  values: ReviewStatus[];
}
export interface SensitivityCondition {
  type: 'sensitivity';
  value: Sensitivity;
}
export interface TagCondition {
  type: 'tag';
  /** Tag keys; any of them. */
  values: string[];
}
export interface FieldCondition {
  type: 'field';
  field: string;
  op: ReportFieldOp;
  /** any_of / none_of: list value keys. */
  values?: string[];
  /** contains / equals. */
  text?: string;
  /** at_least / at_most / between. */
  min?: number;
  max?: number;
  /** in_period: ISO prefixes (YYYY, YYYY-MM, YYYY-MM-DD). */
  from?: string;
  to?: string;
  /** is: entity ids. */
  ids?: string[];
}
export interface RelationCondition {
  type: 'relation';
  /** Relation type keys; empty = any relation. */
  relations: string[];
  /** `out`: this entity is the subject (e.g. "employer of"); `in`: the object ("employed by"); `either`. */
  direction: RelationDirection;
  /** The entity at the other end. */
  other?: EntitySet;
  /** The link's period overlaps this span (ISO prefixes). Undated links match only with `undated`. */
  from?: string;
  to?: string;
  undated?: boolean;
  /** Amount bounds: the stated amount, or a range's lower bound (min) / upper bound (max). */
  amountMin?: number;
  amountMax?: number;
  currency?: string;
  /** Review statuses of the link (default: any but rejected). */
  statuses?: ReviewStatus[];
  /** "has no such link". */
  negate?: boolean;
}
export type ReportCondition = NameCondition | StatusCondition | SensitivityCondition | TagCondition | FieldCondition | RelationCondition;
export type ReportConditionType = ReportCondition['type'];

/** A set of entities: specific ones, and/or those of a kind and types matching conditions. */
export interface EntitySet {
  ids?: string[];
  kind?: EntityKind;
  /** Type keys; sub-types included. */
  types?: string[];
  match?: ReportMatch;
  conditions?: ReportCondition[];
}

export const REPORT_SORTS = ['name', 'code', 'type', 'updated'] as const;
export type ReportSort = (typeof REPORT_SORTS)[number];

export interface ReportQuery {
  v: 1;
  kind: EntityKind;
  types?: string[];
  match: ReportMatch;
  conditions: ReportCondition[];
  /** Extra field columns (field keys). */
  columns?: string[];
  sort?: { by: ReportSort; dir: 'asc' | 'desc' };
}

export const emptyReportQuery = (kind: EntityKind = 'actor'): ReportQuery => ({ v: 1, kind, match: 'all', conditions: [] });

// ─── Validation ──────────────────────────────────────────────────────────────

export type ParseResult<T> = { ok: true; value: T } | { ok: false; error: string };

const ISO_PREFIX = /^\d{4}(-(0[1-9]|1[0-2])(-(0[1-9]|[12]\d|3[01]))?)?$/;
const ID = /^[0-9A-Za-z_-]{1,64}$/;
const CURRENCY = /^[A-Z]{3}$/;

class Invalid extends Error {}
const fail = (msg: string): never => {
  throw new Invalid(msg);
};

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

function text(v: unknown, what: string, required = true): string | undefined {
  if (v === undefined || v === null || v === '') return required ? fail(`${what}: type something to look for.`) : undefined;
  if (typeof v !== 'string') return fail(`${what}: expected text.`);
  const s = v.trim().replace(/\s+/g, ' ');
  if (!s && required) fail(`${what}: type something to look for.`);
  if (s.length > REPORT_LIMITS.text) fail(`${what}: keep it under ${REPORT_LIMITS.text} characters.`);
  return s || undefined;
}

function list<T extends string>(v: unknown, what: string, check: (x: unknown) => x is T, required = true): T[] {
  if (v === undefined || v === null) return required ? fail(`${what}: pick at least one.`) : [];
  if (!Array.isArray(v)) return fail(`${what}: expected a list.`);
  if (v.length > REPORT_LIMITS.items) fail(`${what}: pick at most ${REPORT_LIMITS.items}.`);
  const out = [...new Set(v)];
  for (const x of out) if (!check(x)) fail(`${what}: "${String(x).slice(0, 40)}" is not valid.`);
  if (required && !out.length) fail(`${what}: pick at least one.`);
  return out as T[];
}

function num(v: unknown, what: string): number | undefined {
  if (v === undefined || v === null || v === '') return undefined;
  const n = typeof v === 'number' ? v : Number.NaN;
  if (!Number.isFinite(n)) fail(`${what}: expected a number.`);
  return n;
}

function iso(v: unknown, what: string): string | undefined {
  if (v === undefined || v === null || v === '') return undefined;
  if (typeof v !== 'string' || !ISO_PREFIX.test(v)) return fail(`${what}: use a year (2018), a month (2018-06) or a day (2018-06-30).`);
  return v;
}

const isId = (x: unknown): x is string => typeof x === 'string' && ID.test(x);
const isKeyStr = (x: unknown): x is string => isKey(x);
const isTagKey = (x: unknown): x is string => typeof x === 'string' && /^[a-z0-9][a-z0-9_-]{0,63}$/.test(x);

/** Type ids a type key covers (the type and its sub-types). */
export function typeIdsFor(snap: Pick<SchemaSnapshot, 'types'>, keys: readonly string[]): string[] {
  const roots = snap.types.filter(t => keys.includes(t.key));
  return snap.types.filter(t => typeChain(snap.types, t.id).some(a => roots.some(r => r.id === a.id))).map(t => t.id);
}

/** Fields entities of this kind (and these types) can carry, one per key, for the builder's field picker. */
export function reportFieldsFor(
  snap: Pick<SchemaSnapshot, 'types' | 'groups' | 'fields' | 'templates'>,
  kind: EntityKind,
  types?: readonly string[],
): FieldDef[] {
  const typeDefs = snap.types.filter(t => t.kind === kind && (!types?.length || typeIdsFor(snap, types).includes(t.id)));
  const out = new Map<string, FieldDef>();
  for (const t of typeDefs) for (const g of groupsForType(snap, t.id)) for (const f of g.fields) if (!out.has(f.key)) out.set(f.key, f);
  return [...out.values()];
}

/** Relation types that can touch an entity of this kind, for the builder's relation picker. */
export function reportRelationsFor(snap: Pick<SchemaSnapshot, 'relationTypes'>, kind: EntityKind): RelationTypeDef[] {
  return snap.relationTypes.filter(
    r => !r.archived && (!r.sourceKinds.length || r.sourceKinds.includes(kind) || !r.targetKinds.length || r.targetKinds.includes(kind)),
  );
}

interface Budget {
  n: number;
}

function parseCondition(
  raw: unknown,
  kind: EntityKind | undefined,
  snap: SchemaSnapshot | undefined,
  depth: number,
  b: Budget,
): ReportCondition {
  if (++b.n > REPORT_LIMITS.conditions) fail(`Use at most ${REPORT_LIMITS.conditions} conditions in one report.`);
  if (!isObj(raw)) return fail('Each condition must be an object.');
  switch (raw.type) {
    case 'name':
      return { type: 'name', text: text(raw.text, 'Name') as string };
    case 'status':
      return { type: 'status', values: list(raw.values, 'Review status', isReviewStatus) };
    case 'sensitivity':
      if (!isSensitivity(raw.value)) fail('Sensitivity must be standard or confidential.');
      return { type: 'sensitivity', value: raw.value as Sensitivity };
    case 'tag':
      return { type: 'tag', values: list(raw.values, 'Tag', isTagKey) };
    case 'field':
      return parseField(raw, kind, snap);
    case 'relation':
      return parseRelation(raw, snap, depth, b);
    default:
      return fail(`Unknown condition "${String(raw.type).slice(0, 20)}".`);
  }
}

function parseField(raw: Record<string, unknown>, kind: EntityKind | undefined, snap: SchemaSnapshot | undefined): FieldCondition {
  if (!isKey(raw.field)) fail('Pick a field.');
  const field = raw.field as string;
  if (!(REPORT_FIELD_OPS as readonly string[]).includes(raw.op as string)) fail('Pick how to compare the field.');
  const op = raw.op as ReportFieldOp;
  let def: FieldDef | undefined;
  if (snap) {
    def = (kind ? reportFieldsFor(snap, kind) : snap.fields).find(f => f.key === field) ?? snap.fields.find(f => f.key === field);
    if (!def) fail(`"${field}" is not a field${kind ? ` of ${ENTITY_KIND_LABELS[kind].toLowerCase()}s` : ''}.`);
    if (def && !REPORT_FIELD_OPS_BY_TYPE[def.dataType].includes(op))
      fail(`${def.label}: "${REPORT_FIELD_OP_LABELS[op]}" doesn't apply to this kind of field.`);
  }
  const label = def?.label ?? field;
  const out: FieldCondition = { type: 'field', field, op };
  switch (op) {
    case 'any_of':
    case 'none_of':
      out.values = list(raw.values, label, isKeyStr);
      if (snap && def?.enumList) {
        const known = snap.lists.find(l => l.key === def?.enumList)?.values.map(v => v.key) ?? [];
        for (const v of out.values) if (!known.includes(v)) fail(`${label}: "${v}" is not in the list.`);
      }
      break;
    case 'contains':
    case 'equals':
      out.text = text(raw.text, label);
      break;
    case 'at_least':
      out.min = num(raw.min, label);
      if (out.min === undefined) fail(`${label}: enter a number.`);
      break;
    case 'at_most':
      out.max = num(raw.max, label);
      if (out.max === undefined) fail(`${label}: enter a number.`);
      break;
    case 'between':
      out.min = num(raw.min, label);
      out.max = num(raw.max, label);
      if (out.min === undefined || out.max === undefined) fail(`${label}: enter both numbers.`);
      if ((out.min as number) > (out.max as number)) fail(`${label}: the first number must be the smaller.`);
      break;
    case 'in_period':
      out.from = iso(raw.from, label);
      out.to = iso(raw.to, label);
      if (!out.from && !out.to) fail(`${label}: enter a start or an end.`);
      if (out.from && out.to && out.from > `${out.to}~`) fail(`${label}: the start is after the end.`);
      break;
    case 'is':
      out.ids = list(raw.ids, label, isId);
      break;
  }
  return out;
}

function parseRelation(raw: Record<string, unknown>, snap: SchemaSnapshot | undefined, depth: number, b: Budget): RelationCondition {
  const relations = list(raw.relations ?? [], 'Relation', isKeyStr, false);
  if (snap) for (const r of relations) if (!snap.relationTypes.some(t => t.key === r)) fail(`"${r}" is not a relation type.`);
  if (!(RELATION_DIRECTIONS as readonly string[]).includes(raw.direction as string)) fail('Pick the direction of the link.');
  const out: RelationCondition = { type: 'relation', relations, direction: raw.direction as RelationDirection };
  if (raw.other !== undefined && raw.other !== null) {
    if (depth >= REPORT_LIMITS.depth) fail(`Describe the other end at most ${REPORT_LIMITS.depth} levels deep.`);
    out.other = parseSet(raw.other, snap, depth + 1, b);
  }
  out.from = iso(raw.from, 'Period');
  out.to = iso(raw.to, 'Period');
  if (out.from && out.to && out.from > `${out.to}~`) fail('Period: the start is after the end.');
  if (raw.undated === true) out.undated = true;
  out.amountMin = num(raw.amountMin, 'Amount');
  out.amountMax = num(raw.amountMax, 'Amount');
  if (out.amountMin !== undefined && out.amountMax !== undefined && out.amountMin > out.amountMax)
    fail('Amount: the minimum is above the maximum.');
  if (raw.currency !== undefined && raw.currency !== null && raw.currency !== '') {
    if (typeof raw.currency !== 'string' || !CURRENCY.test(raw.currency.toUpperCase())) fail('Currency: use a 3-letter code (EUR, USD).');
    out.currency = (raw.currency as string).toUpperCase();
  }
  const statuses = list(raw.statuses ?? [], 'Link status', isReviewStatus, false);
  if (statuses.length) out.statuses = statuses;
  if (raw.negate === true) out.negate = true;
  for (const k of ['from', 'to', 'amountMin', 'amountMax', 'currency'] as const) if (out[k] === undefined) delete out[k];
  return out;
}

function parseSet(raw: unknown, snap: SchemaSnapshot | undefined, depth: number, b: Budget): EntitySet {
  if (!isObj(raw)) return fail('The other end must be an object.');
  const out: EntitySet = {};
  if (raw.ids !== undefined) {
    const ids = list(raw.ids, 'Entities', isId, false);
    if (ids.length) out.ids = ids;
  }
  if (raw.kind !== undefined && raw.kind !== null) {
    if (!isEntityKind(raw.kind)) fail('Unknown kind.');
    out.kind = raw.kind as EntityKind;
  }
  const types = parseTypes(raw.types, out.kind, snap);
  if (types.length) out.types = types;
  const conditions = raw.conditions === undefined ? [] : raw.conditions;
  if (!Array.isArray(conditions)) fail('Conditions must be a list.');
  if ((conditions as unknown[]).length) {
    out.match = parseMatch(raw.match);
    out.conditions = (conditions as unknown[]).map(c => parseCondition(c, out.kind, snap, depth, b));
  }
  return out;
}

function parseTypes(raw: unknown, kind: EntityKind | undefined, snap: SchemaSnapshot | undefined): string[] {
  const types = list(raw ?? [], 'Type', isKeyStr, false);
  if (snap)
    for (const k of types) {
      const t = snap.types.find(x => x.key === k);
      if (!t) fail(`"${k}" is not a type.`);
      if (t && kind && t.kind !== kind) fail(`${t.label} is not a type of ${ENTITY_KIND_LABELS[kind].toLowerCase()}.`);
    }
  return types;
}

const parseMatch = (v: unknown): ReportMatch =>
  v === 'any' ? 'any' : v === undefined || v === 'all' ? 'all' : fail('Match must be all or any.');

/**
 * Checks a query from the builder or the API. With the schema, field keys, list values, types and relation types
 * must exist and each operator must suit its field. Unknown properties are dropped.
 */
export function parseReportQuery(raw: unknown, snap?: SchemaSnapshot): ParseResult<ReportQuery> {
  try {
    if (!isObj(raw)) fail('The report query must be an object.');
    const r = raw as Record<string, unknown>;
    if (r.v !== REPORT_QUERY_VERSION) fail('This report was made by a newer version of Hoopoe.');
    if (!isEntityKind(r.kind)) fail('Pick what the report lists: actors, events, places, assets, sources or cases.');
    const kind = r.kind as EntityKind;
    const q: ReportQuery = { v: 1, kind, match: parseMatch(r.match), conditions: [] };
    const types = parseTypes(r.types, kind, snap);
    if (types.length) q.types = types;
    if (!Array.isArray(r.conditions ?? [])) fail('Conditions must be a list.');
    const b: Budget = { n: 0 };
    q.conditions = ((r.conditions ?? []) as unknown[]).map(c => parseCondition(c, kind, snap, 0, b));
    const columns = list(r.columns ?? [], 'Columns', isKeyStr, false);
    if (columns.length > REPORT_LIMITS.columns) fail(`Show at most ${REPORT_LIMITS.columns} extra columns.`);
    if (snap) for (const c of columns) if (!snap.fields.some(f => f.key === c)) fail(`"${c}" is not a field.`);
    if (columns.length) q.columns = columns;
    if (r.sort !== undefined && r.sort !== null) {
      const s = r.sort as Record<string, unknown>;
      if (!isObj(s) || !(REPORT_SORTS as readonly string[]).includes(s.by as string)) fail('Sort by name, ID code, type or updated date.');
      q.sort = { by: s.by as ReportSort, dir: s.dir === 'desc' ? 'desc' : 'asc' };
    }
    return { ok: true, value: q };
  } catch (e) {
    if (e instanceof Invalid) return { ok: false, error: e.message };
    throw e;
  }
}

/** Every entity id a query mentions (to show their names in the builder and the description). */
export function reportEntityIds(q: Pick<ReportQuery, 'conditions'>): string[] {
  const out = new Set<string>();
  const walk = (cs: readonly ReportCondition[] | undefined) => {
    for (const c of cs ?? []) {
      if (c.type === 'field') for (const id of c.ids ?? []) out.add(id);
      if (c.type === 'relation' && c.other) {
        for (const id of c.other.ids ?? []) out.add(id);
        walk(c.other.conditions);
      }
    }
  };
  walk(q.conditions);
  return [...out];
}

// ─── Plain-English description ───────────────────────────────────────────────

const PLURAL: Record<EntityKind, string> = {
  actor: 'Actors',
  event: 'Events',
  location: 'Places',
  asset: 'Assets / transactions',
  document: 'Sources',
  case: 'Cases',
};

const fmtNum = (n: number) => n.toLocaleString('en-GB');
const span = (from?: string, to?: string) => (from && to ? `${from}–${to}` : from ? `from ${from}` : to ? `until ${to}` : '');

/** "employed by" for `in`, "employer of" for `out`; symmetric types read the same both ways. */
export function relationPhrase(
  rt: Pick<RelationTypeDef, 'label' | 'inverseLabel' | 'symmetric'> | undefined,
  key: string,
  dir: RelationDirection,
) {
  if (!rt) return key;
  if (dir === 'in' && !rt.symmetric) return rt.inverseLabel ?? `(inverse of) ${rt.label}`;
  if (dir === 'either' && !rt.symmetric && rt.inverseLabel) return `${rt.label} / ${rt.inverseLabel}`;
  return rt.label;
}

type Names = ReadonlyMap<string, string> | Record<string, string>;
const nameOf = (names: Names | undefined, id: string) =>
  (names instanceof Map ? names.get(id) : (names as Record<string, string> | undefined)?.[id]) ?? id;

function describeSet(s: EntitySet, snap: SchemaSnapshot, names?: Names): string {
  const parts: string[] = [];
  if (s.ids?.length) parts.push(s.ids.map(id => nameOf(names, id)).join(' or '));
  const typeLabels = (s.types ?? []).map(k => snap.types.find(t => t.key === k)?.label ?? k);
  const what = typeLabels.length ? typeLabels.join(' or ') : s.kind ? ENTITY_KIND_LABELS[s.kind].toLowerCase() : '';
  if (what) parts.push(`${s.ids?.length ? '' : 'any '}${what}`.trim());
  if (s.conditions?.length)
    parts.push(`(${s.conditions.map(c => describeReportCondition(c, snap, names)).join(s.match === 'any' ? ' or ' : '; ')})`);
  return parts.join(' ') || 'anything';
}

export function describeReportCondition(c: ReportCondition, snap: SchemaSnapshot, names?: Names): string {
  switch (c.type) {
    case 'name':
      return `name or ID code contains “${c.text}”`;
    case 'status':
      return `review status is ${c.values.join(' or ')}`;
    case 'sensitivity':
      return `sensitivity is ${c.value}`;
    case 'tag':
      return `tagged ${c.values.join(' or ')}`;
    case 'field': {
      const def = snap.fields.find(f => f.key === c.field);
      const label = def?.label ?? c.field;
      const listLabel = (k: string) =>
        (def?.enumList && snap.lists.find(l => l.key === def.enumList)?.values.find(v => v.key === k)?.label) || k;
      switch (c.op) {
        case 'any_of':
        case 'none_of':
          return `${label} ${REPORT_FIELD_OP_LABELS[c.op]} ${(c.values ?? []).map(listLabel).join(', ')}`;
        case 'contains':
        case 'equals':
          return `${label} ${REPORT_FIELD_OP_LABELS[c.op]} “${c.text}”`;
        case 'at_least':
          return `${label} is at least ${fmtNum(c.min ?? 0)}`;
        case 'at_most':
          return `${label} is at most ${fmtNum(c.max ?? 0)}`;
        case 'between':
          return `${label} is between ${fmtNum(c.min ?? 0)} and ${fmtNum(c.max ?? 0)}`;
        case 'in_period':
          return `${label} falls in ${span(c.from, c.to)}`;
        case 'is':
          return `${label} links to ${(c.ids ?? []).map(id => nameOf(names, id)).join(' or ')}`;
        default:
          return `${label} ${REPORT_FIELD_OP_LABELS[c.op]}`;
      }
    }
    case 'relation': {
      const rels = c.relations.length
        ? c.relations
            .map(k =>
              relationPhrase(
                snap.relationTypes.find(t => t.key === k),
                k,
                c.direction,
              ),
            )
            .join(' or ')
        : 'linked to';
      const bits = [`${c.negate ? 'not ' : ''}${rels}`, c.other ? describeSet(c.other, snap, names) : 'anything'];
      const when = span(c.from, c.to);
      if (when) bits.push(`(${when}${c.undated ? ', or undated' : ''})`);
      const cur = c.currency ? ` ${c.currency}` : '';
      if (c.amountMin !== undefined && c.amountMax !== undefined)
        bits.push(`with an amount between ${fmtNum(c.amountMin)} and ${fmtNum(c.amountMax)}${cur}`);
      else if (c.amountMin !== undefined) bits.push(`with an amount of at least ${fmtNum(c.amountMin)}${cur}`);
      else if (c.amountMax !== undefined) bits.push(`with an amount of at most ${fmtNum(c.amountMax)}${cur}`);
      if (c.statuses?.length) bits.push(`(links ${c.statuses.join(' or ')})`);
      return bits.join(' ');
    }
  }
}

/** "Person: employed by any Government body (2018–2022); donated to Cedar Fund with an amount of at least 10,000". */
export function describeReportQuery(q: ReportQuery, snap: SchemaSnapshot, names?: Names): string {
  const typeLabels = (q.types ?? []).map(k => snap.types.find(t => t.key === k)?.label ?? k);
  const head = typeLabels.length ? typeLabels.join(' or ') : PLURAL[q.kind];
  if (!q.conditions.length) return `All ${head.toLowerCase()}`;
  return `${head}: ${q.conditions.map(c => describeReportCondition(c, snap, names)).join(q.match === 'any' ? ', or ' : '; ')}`;
}

// ─── Sections within the report's question ───────────────────────────────────

const questionKey = (q: ReportQuery) => JSON.stringify([q.kind, q.types ?? [], q.match, q.conditions]);

/**
 * The report question a template section's starter question stays within (D395), or null when it needs none: no
 * starter, a report question that lists every entity of its kind (no types, no conditions), or a starter that is the
 * report question. Of the same kind, a row must answer both questions; of another kind (the votes, resolutions and
 * statements of a report about actors), it must be linked, by any relation and in either direction, to at least one
 * entity the report question lists.
 */
export function sectionWithin(starter: ReportQuery | null | undefined, report: ReportQuery): ReportQuery | null {
  if (!starter) return null;
  if (!report.conditions.length && !report.types?.length) return null;
  if (questionKey(starter) === questionKey(report)) return null;
  return report;
}

/**
 * The question a section's rows come from (its starter, or the report question) and what it stays within. A case
 * file's rows are the actors linked to its case (D447), within nothing.
 */
export function sectionQueryOf(
  report: Pick<Report, 'query' | 'sections'>,
  sectionKey: string | null,
): { query: ReportQuery; within: ReportQuery | null } {
  const section = sectionKey ? report.sections.find(s => s.key === sectionKey) : undefined;
  if (section && isCaseSection(section)) return { query: caseSectionQuery(section.entityId as string), within: null };
  const starter = section?.starter;
  return starter ? { query: starter, within: sectionWithin(starter, report.query) } : { query: report.query, within: null };
}

/** "Limited to the report’s question: Actors: Camp is any of Adverse, Pivot." */
export function describeWithin(q: Pick<ReportQuery, 'kind'>, within: ReportQuery, snap: SchemaSnapshot, names?: Names): string {
  const words = describeReportQuery(within, snap, names);
  return q.kind === within.kind
    ? `Limited to the report’s question: ${words}.`
    : `Limited to ${PLURAL[q.kind].toLowerCase()} linked to the report’s question: ${words}.`;
}

// ─── Results ─────────────────────────────────────────────────────────────────

/** A matching link shown next to a row: why the entity matched a relation condition. */
export interface ReportLink {
  /** Index of the top-level condition it answers. */
  condition: number;
  claimId: string;
  predicate: string;
  /** Read from this row's side: "employed by", "donated to". */
  label: string;
  other: { id: string; code: string; displayName: string; kind: EntityKind; typeId: string };
  period: string | null;
  amount: string | null;
  status: ReviewStatus;
  /** Reliability status of the link's claim (Official document / Corroborated / Single source / Hypothesis). */
  reliability: ReliabilityStatus;
}

export interface ReportRow {
  id: string;
  code: string;
  displayName: string;
  kind: EntityKind;
  typeId: string;
  reviewStatus: ReviewStatus;
  sensitivity: Sensitivity;
  updatedAt: number;
  /** Current, non-rejected, not off-record values of the query's columns. */
  fields: Record<string, unknown[]>;
  links: ReportLink[];
}

export interface ReportResult {
  rows: ReportRow[];
  /** All matches (the rows may be cut at the limit). */
  total: number;
  truncated: boolean;
  description: string;
}

/** The fixed words of a results table: its column headers and the words for review status and sensitivity. */
export interface ReportTableWords {
  headers: { name: string; code: string; type: string; links: string; reviewStatus: string; sensitivity: string; updated: string };
  status: Record<ReviewStatus, string>;
  sensitivity: Record<Sensitivity, string>;
  /** After each matching link, in brackets (the link's reliability status). */
  reliability: Record<ReliabilityStatus, string>;
}

/** English, as the CSV/XLSX export prints them; the full report passes its language's (DOSSIER_LABELS, D416). */
export const REPORT_TABLE_WORDS: ReportTableWords = {
  headers: {
    name: 'Name',
    code: 'ID code',
    type: 'Type',
    links: 'Matching links',
    reviewStatus: 'Review status',
    sensitivity: 'Sensitivity',
    updated: 'Updated',
  },
  status: { unreviewed: 'Unreviewed', verified: 'Verified', rejected: 'Rejected', disputed: 'Disputed' },
  sensitivity: { standard: 'standard', confidential: 'confidential' },
  reliability: RELIABILITY_LABELS.en,
};

export const linkText = (l: Pick<ReportLink, 'label' | 'other' | 'period' | 'amount'>) =>
  `${l.label} ${l.other.displayName}${l.period ? ` (${l.period})` : ''}${l.amount ? ` · ${l.amount}` : ''}`;

/**
 * The flat table of a result, as exported (CSV, XLSX): header labels, then one row per entity. `words`: the fixed
 * headers and words in the export's language (English by default); the schema's field and type labels are data.
 */
export function reportTable(
  q: ReportQuery,
  result: Pick<ReportResult, 'rows'>,
  snap: SchemaSnapshot,
  words: ReportTableWords = REPORT_TABLE_WORDS,
) {
  const defs = (q.columns ?? []).map(k => snap.fields.find(f => f.key === k)).filter((f): f is FieldDef => !!f);
  const hasLinks = q.conditions.some(c => c.type === 'relation' && !c.negate);
  const h = words.headers;
  const headers = [
    h.name,
    h.code,
    h.type,
    ...defs.map(d => d.label),
    ...(hasLinks ? [h.links] : []),
    h.reviewStatus,
    h.sensitivity,
    h.updated,
  ];
  const rows = result.rows.map(r => [
    r.displayName,
    r.code,
    snap.types.find(t => t.id === r.typeId)?.label ?? r.typeId,
    ...defs.map(d => (r.fields[d.key] ?? []).map(v => formatFieldValue(d, v, snap)).join('; ')),
    ...(hasLinks ? [r.links.map(l => `${linkText(l)} [${words.reliability[l.reliability]}]`).join('; ')] : []),
    words.status[r.reviewStatus] ?? r.reviewStatus,
    words.sensitivity[r.sensitivity] ?? r.sensitivity,
    new Date(r.updatedAt).toISOString().slice(0, 10),
  ]);
  return { headers, rows };
}

// ─── Saved reports and templates ─────────────────────────────────────────────

/**
 * One section of a report template: a heading, what to write in it, and optionally a query to start from. A report's
 * own copy may also hold **case files** (`kind: 'case'`, D447): one chapter about one entity or event, drafted from
 * every claim about it; a template never carries one (the case is chosen on the report).
 */
export interface ReportTemplateSection {
  key: string;
  title: string;
  guidance: string;
  starter?: ReportQuery | null;
  /**
   * 'case': a case file about `entityId` (case-file.ts); 'hypotheses': the scorecard (hypotheses.ts); 'matrix',
   * 'leads', 'coverage': the synthesis and annexes (synthesis.ts). Absent on ordinary sections.
   */
  kind?: 'case' | 'hypotheses' | 'matrix' | 'leads' | 'coverage';
  entityId?: string;
}

/** Whether a section is a case file. */
export const isCaseSection = (s: Partial<ReportTemplateSection> | null | undefined): boolean =>
  s?.kind === 'case' && typeof s.entityId === 'string' && s.entityId.length > 0;

/** The section key of a case file: `case_` and the entity id, in the key alphabet (ids are ULIDs, so it stays unique). */
export const caseSectionKey = (entityId: string) => `case_${entityId.toLowerCase().replace(/[^a-z0-9]/g, '')}`.slice(0, 48);

/** The guidance a case file is drafted with (the AI's brief; the fixed headings are added by the app). */
export const CASE_SECTION_GUIDANCE =
  'A case file about one entity or event: what the records establish about it, told from start to finish, then what remains unproven and what to check next.';

/**
 * The question a case file's rows come from: the actors linked to the case (any relation, either way). It feeds the
 * figures drawn on the case (the graph of its neighbourhood) and the Advanced results; the facts table itself is
 * read from the case's own claims.
 */
export const caseSectionQuery = (entityId: string): ReportQuery => ({
  v: 1,
  kind: 'actor',
  match: 'all',
  conditions: [{ type: 'relation', relations: [], direction: 'either', other: { ids: [entityId] } }],
});

/**
 * The report's sections with a case file added: after the last case file, else before the "patterns" or
 * "recommendations" chapter of the Influence mapping report (the synthesis follows the cases), else last. A case
 * already there is left as it is.
 */
export function insertCaseSection(sections: readonly ReportTemplateSection[], section: ReportTemplateSection): ReportTemplateSection[] {
  if (sections.some(s => s.key === section.key)) return [...sections];
  const out = [...sections];
  const lastCase = out.map(s => isCaseSection(s)).lastIndexOf(true);
  let at = lastCase >= 0 ? lastCase + 1 : out.findIndex(s => s.key === 'patterns' || s.key === 'recommendations');
  if (at < 0) at = out.length;
  out.splice(at, 0, section);
  return out;
}

export interface ReportTemplate {
  id: string;
  key: string;
  name: string;
  description: string | null;
  sections: ReportTemplateSection[];
  archived: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface Report {
  id: string;
  name: string;
  description: string | null;
  query: ReportQuery;
  templateId: string | null;
  /** The template's sections as they were when the report was made. */
  sections: ReportTemplateSection[];
  shared: boolean;
  /** The language the report is written in: its AI findings are drafted in it and its full report printed in it. */
  language: ReportLanguage;
  createdBy: string | null;
  ownerName: string | null;
  createdAt: number;
  updatedAt: number;
  /** The owner (or an admin) may change or delete it. */
  canEdit: boolean;
}

export const REPORT_TEMPLATE_LIMITS = { sections: 20, title: 120, guidance: 2000 } as const;

/** Checks an admin's template (name, description, sections with unique keys). */
export function parseReportTemplate(
  raw: unknown,
  snap?: SchemaSnapshot,
): ParseResult<Pick<ReportTemplate, 'name' | 'description' | 'sections'>> {
  try {
    if (!isObj(raw)) fail('The template must be an object.');
    const r = raw as Record<string, unknown>;
    const name = text(r.name, 'Name') as string;
    if (name.length > REPORT_LIMITS.name) fail(`Name: keep it under ${REPORT_LIMITS.name} characters.`);
    const description = typeof r.description === 'string' ? r.description.trim().slice(0, REPORT_LIMITS.description) || null : null;
    if (!Array.isArray(r.sections) || !r.sections.length) fail('Add at least one section.');
    const sectionsRaw = r.sections as unknown[];
    if (sectionsRaw.length > REPORT_TEMPLATE_LIMITS.sections) fail(`Use at most ${REPORT_TEMPLATE_LIMITS.sections} sections.`);
    const keys = new Set<string>();
    const sections = sectionsRaw.map((s, i): ReportTemplateSection => {
      if (!isObj(s)) return fail(`Section ${i + 1} must be an object.`);
      const title = typeof s.title === 'string' ? s.title.trim().replace(/\s+/g, ' ') : '';
      if (!title) fail(`Section ${i + 1}: give it a title.`);
      if (title.length > REPORT_TEMPLATE_LIMITS.title)
        fail(`Section ${i + 1}: keep the title under ${REPORT_TEMPLATE_LIMITS.title} characters.`);
      let key = typeof s.key === 'string' && isKey(s.key) ? s.key : sectionKey(title);
      while (keys.has(key)) key = `${key.slice(0, 44)}_${i + 1}`;
      keys.add(key);
      const guidance = typeof s.guidance === 'string' ? s.guidance.trim() : '';
      if (guidance.length > REPORT_TEMPLATE_LIMITS.guidance)
        fail(`Section ${i + 1}: keep the guidance under ${REPORT_TEMPLATE_LIMITS.guidance} characters.`);
      let starter: ReportQuery | null = null;
      if (s.starter !== undefined && s.starter !== null) {
        const p = parseReportQuery(s.starter, snap);
        if (!p.ok) fail(`Section ${i + 1}: ${p.error}`);
        else starter = p.value;
      }
      return { key, title, guidance, starter };
    });
    return { ok: true, value: { name, description, sections } };
  } catch (e) {
    if (e instanceof Invalid) return { ok: false, error: e.message };
    throw e;
  }
}

const sectionKey = (title: string) =>
  title
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^\w\s]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/^[^a-z]+/, '')
    .slice(0, 48) || 'section';

/**
 * The seeded template, from the structure of the sample synthesis note (evals/real "manual reporting"): central
 * actors and networks, the financial chain, mechanisms, strengths and weaknesses, and the provisional power balance.
 */
export const SYNTHESIS_NOTE_TEMPLATE: Pick<ReportTemplate, 'id' | 'key' | 'name' | 'description' | 'sections'> = {
  id: 'rtpl_synthesis_note',
  key: 'synthesis_note',
  name: 'Synthesis note',
  description:
    'Initial findings on an ecosystem, in the order of the program’s synthesis notes: who the actors are and how they connect, how money moves, the mechanisms at work, strengths and weaknesses, and a provisional reading of the balance of power.',
  sections: [
    {
      key: 'actors_networks',
      title: 'Central actors and transnational networks',
      guidance:
        'Which actors structure the ecosystem, country by country and across borders, and the roles they play for each other (funding, legislative lobbying, media, legal action). Name the hubs and the relays.',
      starter: {
        v: 1,
        kind: 'actor',
        match: 'all',
        conditions: [
          {
            type: 'relation',
            relations: ['member_of', 'coalition_with', 'sister_organization_of', 'event_partner_of', 'role_at'],
            direction: 'either',
          },
        ],
        columns: ['camp'],
      },
    },
    {
      key: 'financial_chain',
      title: 'The financial chain',
      guidance:
        'How money moves through the system: producers, financiers (banks, funds, insurers), public money and research programmes, philanthropy and tax mechanisms. Give amounts and periods where known, and say when they are estimated or undisclosed.',
      starter: {
        v: 1,
        kind: 'actor',
        match: 'all',
        conditions: [
          {
            type: 'relation',
            relations: ['funded_by', 'owner_of', 'donated_to', 'paid', 'invested_in', 'granted'],
            direction: 'either',
          },
        ],
        columns: ['camp'],
      },
    },
    {
      key: 'mechanisms',
      title: 'Mechanisms of influence',
      guidance:
        'The mechanisms the actors use, public and covert: lobbying, legal and administrative pressure, censorship and criminalization, communication campaigns and polling. Separate documented facts from allegations and assessments.',
      starter: {
        v: 1,
        kind: 'actor',
        match: 'all',
        conditions: [{ type: 'field', field: 'modes_of_influence', op: 'exists' }],
        columns: ['camp'],
      },
    },
    {
      key: 'strengths_weaknesses',
      title: 'Strengths and weaknesses',
      guidance:
        'Identified strengths (social base, coordination, legal expertise, repertoire of action) and weaknesses or constraints (repression, political blockage, fragmentation, resources) of the actors this note is about.',
      starter: {
        v: 1,
        kind: 'actor',
        match: 'all',
        conditions: [{ type: 'field', field: 'vulnerabilities', op: 'exists' }],
        columns: ['camp'],
      },
    },
    {
      key: 'power_balance',
      title: 'Provisional power balance',
      guidance:
        'A provisional assessment, side by side: organizational strength, narrative strength and the capacity to act across levels (local, national, European). This is analyst judgement: label it as an assessment.',
      starter: {
        v: 1,
        kind: 'actor',
        match: 'all',
        conditions: [{ type: 'field', field: 'influence_ratings', op: 'exists' }],
        columns: ['camp', 'influence_ratings'],
      },
    },
  ],
};

/** Event types of direct harms (migration 0052): the Harms chapter of the Influence mapping report lists them. */
export const HARM_EVENT_TYPES = ['legal_action', 'surveillance', 'online_interference'] as const;

/**
 * The full-report template, from the structure of a long-form influence mapping report: the levels of the system,
 * the actors at each level, the network, the money, the pivots, the points of impact, the harms, patterns and the
 * analyst's recommendations. Seeded by migration 0046.
 */
export const INFLUENCE_MAPPING_TEMPLATE: Pick<ReportTemplate, 'id' | 'key' | 'name' | 'description' | 'sections'> = {
  id: 'rtpl_influence_mapping',
  key: 'influence_mapping',
  name: 'Influence mapping report',
  description:
    'A long-form report on a system of influence, exported as a full report: how the system is organized by level, who sits where, how actors connect and fund each other, who the pivots are, where the system weighs on decisions, the harms it causes, the recurring patterns and what to do about them.',
  sections: [
    {
      key: 'architecture',
      title: 'Systemic architecture: the levels',
      guidance:
        'Describe the system as a whole before naming individual actors: the strategic level that sets the direction, the operational level that turns it into programmes and money, the relays that carry it to the public and to decision-makers, the field actors who act on the ground, and the repressive level that sanctions, prosecutes or watches. Say how the levels hand work to each other and where the system is centralized or spread out.',
      starter: {
        v: 1,
        kind: 'actor',
        match: 'all',
        conditions: [{ type: 'field', field: 'level', op: 'exists' }],
        columns: ['level', 'level_of_action'],
      },
    },
    {
      key: 'actors_by_level',
      title: 'Actors by level',
      guidance:
        'Go through the levels one at a time and present the main actors of each: what they are, where they work, their camp and the role they play at that level. Keep one paragraph per actor for the most important ones and group the minor ones. Note actors that work at more than one level.',
      starter: {
        v: 1,
        kind: 'actor',
        match: 'all',
        conditions: [{ type: 'field', field: 'level', op: 'exists' }],
        columns: ['level', 'camp'],
      },
    },
    {
      key: 'network',
      title: 'Network and central actors',
      guidance:
        'How the actors are tied together: memberships, coalitions, sister organizations, shared events and people holding roles in several organizations. Name the hubs (the most connected actors) and the brokers that join otherwise separate groups, and say what each tie is used for.',
      starter: {
        v: 1,
        kind: 'actor',
        match: 'all',
        conditions: [
          {
            type: 'relation',
            relations: ['member_of', 'coalition_with', 'sister_organization_of', 'event_partner_of', 'role_at'],
            direction: 'either',
          },
        ],
        columns: ['level', 'camp'],
      },
    },
    {
      key: 'financial_chain',
      title: 'The financial chain',
      guidance:
        'Follow the money from its sources to the actors it pays for: owners, funders, donations, grants, payments and investments. Give amounts, currencies and periods where known, never convert currencies, and say when an amount is estimated or undisclosed. Point out the actors through which most of the money passes.',
      starter: {
        v: 1,
        kind: 'actor',
        match: 'all',
        conditions: [
          {
            type: 'relation',
            relations: ['funded_by', 'owner_of', 'donated_to', 'paid', 'invested_in', 'granted'],
            direction: 'either',
          },
        ],
        columns: ['level', 'camp'],
      },
    },
    {
      key: 'pivot_actors',
      title: 'Pivot actors and their responsibilities',
      guidance:
        'The persons who could move either way, or who hold the positions where decisions are made: what office or role they hold, how their documented votes and statements line up (their alignment score, 0 to 2), and what they are responsible for. Separate documented facts from assessments.',
      starter: {
        v: 1,
        kind: 'actor',
        types: ['person'],
        match: 'all',
        conditions: [{ type: 'field', field: 'camp', op: 'any_of', values: ['pivot'] }],
        columns: ['camp', 'alignment_score', 'current_position'],
      },
    },
    {
      key: 'points_of_impact',
      title: 'Points of impact: votes, questions, positions',
      guidance:
        'Where the system weighs on decisions: votes, parliamentary questions, resolutions and public positions. For each, give the date, the outcome and who voted or spoke which way, and connect it to the actors and money described above.',
      starter: {
        v: 1,
        kind: 'event',
        // Votes only until migration 0050.
        types: ['vote', 'resolution', 'statement'],
        match: 'all',
        conditions: [],
        columns: ['date', 'outcome'],
      },
    },
    {
      key: 'harms',
      title: 'Direct harms: legal pressure, surveillance, interference',
      guidance:
        'The direct harms done to people and organizations: lawsuits and legal threats, administrative pressure, surveillance, smear campaigns, funding cuts and interference in elections or institutions. Name who did what to whom, with dates, and mark allegations clearly as allegations.',
      // Harm events (migration 0052, D399): who carried each one out and who it targeted are its participants.
      starter: {
        v: 1,
        kind: 'event',
        types: [...HARM_EVENT_TYPES, 'controversy'],
        match: 'all',
        conditions: [],
        columns: ['date', 'description', 'outcome'],
      },
    },
    {
      key: 'patterns',
      title: 'Patterns and case studies',
      guidance:
        'The patterns that recur across the system, each illustrated by one or two case studies told from start to finish: the same people moving between organizations, the same funders behind several relays, the same sequence of campaign, pressure and vote.',
      starter: null,
    },
    {
      key: 'recommendations',
      title: 'Recommendations',
      guidance:
        'Written by the analyst, not drafted by AI: what should be done, by whom, and in what order, following from the findings above. Keep each recommendation short and tie it to the section it comes from.',
      starter: null,
    },
  ],
};
